import { Color } from "@skillscratch/shared-constants";
import { memo } from "react";
import classes from "./GameGrid.module.scss";
import "./GameGrid.scss";
import { SquareContent } from "./SquareContent";


export interface Coordinates {
  x: number;
  y: number;
}

export interface GridColorScheme {
  /**
   * An array of colors to use for the grid tiles.
   * The first color is the color of the tile with distance 1 from the treasure.
   * The second color is the color of the tile with distance 2 from the treasure.
   * And so on.
   */
  tileColors: { background: string; text: string }[];
  /**
   * A string representing the url of the pattern to overlay on the grid tiles.
   * The pattern must be a square image.
   * This string will be used in css for the `background` property.
   */
  overlayPattern: string;
  overlayColor: string;
  /**
   * The color of the scratches
   */
  scratchColor: string;
}

export interface GameGridProps {
  scratchable: boolean;
  grid: SquareInfo[][];
  colorScheme: GridColorScheme;
  onSquareReveal?: (coordinates: Coordinates) => void;
  onRectChange?: (rect: DOMRect) => void;
}

export function GameGrid({
  scratchable,
  grid,
  colorScheme,
  onSquareReveal,
}: GameGridProps) {
  const squareSize = `calc(96cqw / ${grid[0].length})`;
  const gapSize = `calc(4cqw / ${grid[0].length - 1})`;
  const offsetPerItem = `calc(${squareSize} + ${gapSize})`;

  return (
    <div
      style={{
        containerType: "inline-size",
      }}
    >
      <div
        style={{
          position: "relative",
          display: "grid",
          gridTemplateColumns: `repeat(${grid[0].length}, ${squareSize})`,
          gridTemplateRows: `repeat(${grid.length}, ${squareSize})`,
          alignContent: "space-between",
          justifyContent: "space-between",
          width: "100%",
          boxSizing: "border-box",
          background: Color.gold,
          fontSize: "4.5cqw",
          fontFamily: "TAN-HEADLINE",
          aspectRatio: "1",
        }}
      >
        {grid.map((row, rowIndex) =>
          row.map((info, squareIndex) => (
            <Square
              // biome-ignore lint/suspicious/noArrayIndexKey: <explanation>
              key={`${rowIndex},${squareIndex}`}
              scratchable={scratchable}
              info={info}
              colorScheme={colorScheme}
              gridWidth="100cqw"
              pixelCoordinatesX={`calc(${squareIndex} * ${offsetPerItem})`}
              pixelCoordinatesY={`calc(${rowIndex} * ${offsetPerItem})`}
              onReveal={() => onSquareReveal?.({ x: squareIndex, y: rowIndex })}
            />
          ))
        )}
      </div>
    </div>
  );
}

interface HiddenSquareInfo {
  type: "hidden";
}

interface LoadingSquareInfo {
  type: "loading";
}

interface TreasureSquareInfo {
  type: "treasure";
  distance: number;
  disabled?: boolean;
}

interface EmptySquareInfo {
  type: "empty";
  distance: number;
  disabled?: boolean;
}

interface BlownUpSquareInfo {
  type: "blownUp";
  disabled?: boolean;
}

export type SquareInfo =
  | HiddenSquareInfo
  | LoadingSquareInfo
  | TreasureSquareInfo
  | EmptySquareInfo
  | BlownUpSquareInfo;

export interface ClickableSquareProps {
  scratchable: boolean;
  info: SquareInfo;
  colorScheme: GridColorScheme;
  gridWidth: string;
  pixelCoordinatesX: string;
  pixelCoordinatesY: string;
  onReveal: () => void;
}

const Square = memo(function Square({
  scratchable,
  info,
  colorScheme,
  gridWidth,
  pixelCoordinatesX,
  pixelCoordinatesY,
  onReveal,
}: ClickableSquareProps) {
  const tileColor =
    "distance" in info
      ? colorScheme.tileColors.at(info.distance - 1) ??
        colorScheme.tileColors.at(-1)
      : undefined;

  const tileBackground: string = (() => {
    switch (info.type) {
      case "hidden": 
      case "loading":
        return colorScheme.scratchColor;
      case "empty":
        return tileColor?.background as string;
      case "treasure":
        return "linear-gradient(135deg, #FFE56B 0%, #FFB449 100%)";
      case "blownUp": 
        return "#f6f6f6";
    }
  })();

  return (
    <div
      style={{
        position: "relative",
        alignContent: "center",
        textAlign: "center",
        background: tileBackground,
        color: tileColor?.text,
        aspectRatio: "1",
        filter:
          "disabled" in info && info.disabled ? "grayscale(1)" : undefined,
      }}
    >
      <SquareContent 
        info={info}
        tileColor={tileColor}
        colorScheme={colorScheme}
      />
      <div
        style={{
          position: "absolute",
          inset: "0",
          background: colorScheme.overlayColor,
          transitionProperty: "opacity",
          transitionDuration:
            "disabled" in info && info.disabled ? "1s" : "200ms",
          transitionTimingFunction: "ease-in",
          opacity: info.type === "hidden" ? 1 : 0,
          overflow: "hidden",
        }}
      >
        <ScratchSquare
          scratchable={scratchable && info.type === "hidden"}
          gridWidth={gridWidth}
          colorScheme={colorScheme}
          pixelCoordinatesX={pixelCoordinatesX}
          pixelCoordinatesY={pixelCoordinatesY}
          onReveal={onReveal}
        />
      </div>
    </div>
  );
});

function ScratchSquare({
  scratchable,
  gridWidth,
  colorScheme,
  pixelCoordinatesX,
  pixelCoordinatesY,
  onReveal,
}: {
  scratchable: boolean;
  gridWidth: string;
  colorScheme: GridColorScheme;
  pixelCoordinatesX: string;
  pixelCoordinatesY: string;
  onReveal: () => void;
}) {
  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: <explanation>
    <div
      className={classes.square}
      style={{
        width: "100%",
        height: "100%",
        backgroundImage: `url("${colorScheme.overlayPattern}")`,
        backgroundSize: `${gridWidth} ${gridWidth}`,
        backgroundRepeat: "no-repeat",
        backgroundPositionX: `calc(-1 * ${pixelCoordinatesX})`,
        backgroundPositionY: `calc(-1 * ${pixelCoordinatesY})`,
        overflow: "hidden",
      }}
      onClick={() => {
        if (scratchable) {
          onReveal();
        }
      }}
    />
  );
}